import { Box, Button, Fade, styled } from '@mui/material'
import { useState } from 'react'
import { useEffectOnce } from 'react-use'
import pbTheme from '../../../../config/theme'
import useGAEvent from '../../../auth/hooks/useGAEvent'
import { GenericHeader } from '../../homepage/components/elements/StepHeader'
import { MonthlyPlans, YearlyPlans } from './components/PlanDetails'
import useProfilePlan from '../../../buckets/hooks/useProfilePlan'

const PlansSection = styled(Box)(({ theme }) => ({
  margin: '40px 0',
  textAlign: 'center',
  padding: '0 15px 10px',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    padding: '0 125px 10px',
    margin: '40px 0 140px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '0 75px 10px',
    margin: '40px 0 80px'
  },
  [theme.breakpoints.up('lg')]: {
    padding: '0 140px 10px'
  }
}))

interface Props {
  hideTitle?: boolean
  plan?: string
  planCycle?: string
  changePlan?: boolean
  pendingPlan?: string
}

type PlanId = 'expert' | 'storage' | 'sharing' | 'no' | 'no-plan' | 'free'

const PlanMapping: Record<string, PlanId> = {
  lite: 'storage',
  backup: 'storage',
  storage: 'storage',
  sharing: 'sharing',
  plus: 'sharing',
  premium: 'expert',
  unlimited: 'expert',
  'unlimited-photobucket': 'expert',
  expert: 'expert',
  no: 'no-plan',
  freemium: 'free'
}

export default function Plans({ hideTitle, plan = undefined, planCycle = undefined, changePlan = false, pendingPlan = undefined }: Readonly<Props>) {
  const { trackEvent } = useGAEvent()
  const cadence = planCycle === 'annual' ? 'yearly' : 'monthly'
  const [plans, setPlans] = useState<'monthly' | 'yearly'>(cadence)
  const { isFreemiumUser } = useProfilePlan()

  const mappedPlanId = plan ? PlanMapping[plan.toLowerCase()] : undefined

  const handleClick = (show: 'monthly' | 'yearly') => () => {
    setPlans(show)
  }

  // capture event for page view
  useEffectOnce(() => {
    trackEvent('plans_page_view', { id: mappedPlanId === undefined ? 'select_plan' : 'change_plan' })
  })

  return (
    <PlansSection component="section" id="plansBlock">
      {!hideTitle && <GenericHeader title={plan === undefined || plan === 'no' ? 'Select a Plan' : 'Change Your Plan'} />}
      <Box
        sx={{
          borderRadius: '8px',
          padding: { xs: '4px 6px', md: '6px 8px', lg: '8px 10px', xl: '10px 12px' },
          minWidth: { xs: '200px', md: '200px', lg: '250px', xl: '300px' },
          marginTop: '20px',
          marginBottom: '20px',
          display: 'inline-flex',
          gap: '10px',
          [pbTheme.breakpoints.up('xl')]: {
            marginBottom: '50px'
          }
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            border: (theme) => `2px solid ${theme.palette.text.disabled}`,
            borderRadius: '86px',
            backgroundColor: '#FFFFFF',
            padding: '2px',
            minWidth: '300px'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '2px',
              left: '2px',
              width: 'calc(50% - 2px)',
              height: 'calc(100% - 4px)',
              backgroundColor: '#1A23B8',
              borderRadius: '86px',
              transition: 'transform 0.2s ease-in-out',
              zIndex: 1
            }}
            style={{
              transform: `translateX(${plans === 'monthly' ? 0 : 'calc(100% - 4px)'})`
            }}
          />
          <Button
            disableRipple
            variant="text"
            sx={{
              color: plans === 'monthly' ? '#ffffff' : '#1A23B8',
              position: 'relative',
              zIndex: 2,
              flex: 1,
              padding: '8px 40px',
              fontSize: '16px',
              fontWeight: 500,
              minHeight: '36px'
            }}
            onClick={handleClick('monthly')}
          >
            Monthly
          </Button>
          <Button
            disableRipple
            variant="text"
            sx={{
              color: plans === 'yearly' ? '#ffffff' : '#1A23B8',
              position: 'relative',
              zIndex: 2,
              flex: 1,
              padding: '8px 40px',
              fontSize: '16px',
              fontWeight: 500,
              minHeight: '36px'
            }}
            onClick={handleClick('yearly')}
          >
            Yearly
          </Button>
        </Box>
      </Box>
      <div style={{ width: '100%', display: 'grid' }}>
        <Fade in={plans === 'monthly'} unmountOnExit>
          <div>
            <MonthlyPlans
              key="monthly"
              plan={mappedPlanId}
              isMonthly={planCycle?.toLowerCase().includes('month') || planCycle?.toLowerCase().includes('trial')}
              changePlan={changePlan}
              pendingPlan={pendingPlan}
              isFreemiumUser={isFreemiumUser}
            />
          </div>
        </Fade>
        <Fade in={plans === 'yearly'} unmountOnExit>
          <div>
            <YearlyPlans
              key="yearly"
              plan={mappedPlanId}
              isYearly={planCycle?.toLowerCase().includes('annual') || planCycle?.toLowerCase().includes('yearly')}
              changePlan={changePlan}
              pendingPlan={pendingPlan}
              isFreemiumUser={isFreemiumUser}
            />
          </div>
        </Fade>
      </div>
    </PlansSection>
  )
}
